import React from 'react';
import { Wrench, Shield, Calendar, Zap, CheckCircle, Users, ArrowRight, Lightbulb, Droplets, Wind, Sun } from 'lucide-react';

const Services = () => {
  const services = [
    {
      title: 'Mobile Rust Removal',
      description: 'On-site rust removal for automotive, marine, and heavy machinery. We come to you.',
      icon: Wrench,
      features: ['On-site service', 'All equipment types', 'Same-day availability', 'Professional equipment']
    },
    {
      title: 'Protective Anti-Rust Coatings',
      description: 'Advanced coating systems to prevent future corrosion and extend equipment life.',
      icon: Shield,
      features: ['10-15 year warranty', 'Marine-grade protection', 'Industrial applications', 'Premium materials']
    },
    {
      title: 'Preventative Maintenance Plans',
      description: 'Scheduled maintenance programs to keep your assets rust-free year-round.',
      icon: Calendar,
      features: ['Custom schedules', 'Regular inspections', 'Cost savings', 'Priority service']
    },
    {
      title: 'Industrial Equipment Restoration',
      description: 'Complete restoration of corroded industrial equipment to like-new condition.',
      icon: Zap,
      features: ['Heavy machinery', 'Structural steel', 'Complete restoration', 'Quality guarantee']
    },
    {
      title: 'Surface Sealing & Barriers',
      description: 'Long-term surface protection with premium barrier applications.',
      icon: CheckCircle,
      features: ['Advanced sealants', 'Weather resistant', 'Chemical protection', 'Long-lasting']
    },
    {
      title: 'Emergency Rust Response',
      description: '24/7 emergency response for critical rust damage and equipment failure prevention.',
      icon: Users,
      features: ['24/7 availability', 'Rapid response', 'Emergency repairs', 'Critical equipment']
    },
  ];

  const rustPreventionTips = [
    {
      title: 'Keep It Clean',
      description: 'Regular cleaning removes dirt, salt, and debris that can trap moisture and accelerate corrosion.',
      icon: Droplets,
      tips: [
        'Wash equipment weekly in harsh environments',
        'Use fresh water to rinse off salt and chemicals',
        'Pay attention to hidden areas and crevices',
        'Dry thoroughly after washing'
      ]
    },
    {
      title: 'Control Moisture',
      description: 'Moisture is rust\'s best friend. Keep your equipment dry whenever possible.',
      icon: Wind,
      tips: [
        'Store equipment in dry, ventilated areas',
        'Use dehumidifiers in storage spaces',
        'Apply moisture-displacing sprays',
        'Ensure proper drainage around equipment'
      ]
    },
    {
      title: 'Regular Inspections',
      description: 'Early detection of rust spots can save thousands in repair costs.',
      icon: Lightbulb,
      tips: [
        'Inspect equipment monthly for rust spots',
        'Check bolts, joints, and welds carefully',
        'Look for paint chips or scratches',
        'Document any changes with photos'
      ]
    },
    {
      title: 'Protective Coatings',
      description: 'Quality protective coatings are your first line of defense against corrosion.',
      icon: Shield,
      tips: [
        'Apply primer before painting metal surfaces',
        'Use marine-grade paints in harsh environments',
        'Reapply coatings as recommended',
        'Choose UV-resistant formulations'
      ]
    },
    {
      title: 'Environmental Protection',
      description: 'Minimize exposure to harsh environmental conditions when possible.',
      icon: Sun,
      tips: [
        'Use covers during extended storage',
        'Park vehicles in garages when possible',
        'Apply wax or sealants for extra protection',
        'Avoid prolonged exposure to salt air'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center w-full bg-gray-900 dark:bg-black">
          {/* Main hero content - Centred like other pages for consistency */}
          <div className="relative z-10 px-6 lg:px-8 py-20 text-center text-white w-full max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our <span className="text-orange-400">Services</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Comprehensive rust protection solutions for all your equipment needs
            </p>
          </div>
        </section>

        {/* Services Grid Section */}
        <section className="py-20 px-6 lg:px-8 bg-white dark:bg-gray-900">
          {/* Inner container to center and constrain the content width */}
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <service.icon className="h-12 w-12 mb-4 text-orange-600 dark:text-orange-400" />
                  <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                        <CheckCircle className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="flex items-center space-x-2 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors duration-200">
                    <span>Learn More</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Rust Prevention Tips Section */}
        {/* Added bottom margin to ensure clear separation from footer */}
        <section className="py-20 pb-24 px-6 lg:px-8 bg-gray-50 dark:bg-gray-800">
          {/* Inner container to center and constrain the content width */}
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
                Rust Prevention <span className="text-orange-600 dark:text-orange-400">Tips</span>
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Keep your valuable equipment rust-free with these expert tips and best practices from our 15+ years of experience
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rustPreventionTips.map((tip, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg">
                  <tip.icon className="h-12 w-12 mb-4 text-orange-600 dark:text-orange-400" />
                  <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                    {tip.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {tip.description}
                  </p>
                  <ul className="space-y-2">
                    {tip.tips.map((tipItem, tipIndex) => (
                      <li key={tipIndex} className="flex items-start text-sm text-gray-600 dark:text-gray-300">
                        <CheckCircle className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-2 flex-shrink-0 mt-0.5" />
                        {tipItem}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <div className="bg-orange-600 dark:bg-orange-700 text-white p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-4">Need Professional Help?</h3>
                <p className="text-lg mb-6 opacity-90">
                  While these tips help prevent rust, sometimes professional intervention is needed. Contact us for a free assessment.
                </p>
                <button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
                  Schedule Free Assessment
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
  );
};

export default Services;
